import React, { useEffect } from 'react';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import dayjs from 'dayjs';

import { dateCellRenderer } from '@/pages/ContractDetails/ContractLines/grid/helper';
import { DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { validator } from './validation/validator';
import { CONTRACT_STATES_COLORS } from '@/config/statuses.config';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { DNR_TYPE } from '@/config/reasonTypes.config';
import NoFurtherBillingToggle from './NoFurtherBillingToggle/NoFurtherBillingToggle';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { Pill } from '@/components/common/Pill/Pill';

export function useColumns(
  dnrContractLines: ContractLine[],
  getValidationErrors?: (colDef: any, data: any, context: any) => string[]
): ColDef<ContractLine>[] {
  const { getReasonCodes, isLoading, reasonCodes } = useGetReasonCodes();

  useEffect(() => {
    getReasonCodes(DNR_TYPE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCellRendererParams = ({ colDef, data, context }: ICellRendererParams) => ({
    errors: getValidationErrors 
      ? getValidationErrors(colDef, data, { contract: context?.contract })
      : validator.getErrorsAt(colDef?.field!, data, { contract: context?.contract }),
    errorTitle: colDef!.headerName
  });

  return [
    {
      colId: 'status',
      headerName: 'Status',
      field: 'status',
      autoHeight: true,
      maxWidth: 140,
      suppressSizeToFit: true,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
      },
      cellRenderer: ({ value }: ICellRendererParams<ContractLine, ContractLine['status']>) => {
        const color = value?.status && CONTRACT_STATES_COLORS[value.status];

        return (
          <Pill background={color} size="large">
            {value?.formattedStatus}
          </Pill>
        );
      },
    },
    {
      colId: 'stockCode',
      headerName: 'Stock Code',
      field: 'product.stockCode',
      initialWidth: 295,
      minWidth: 280,
      cellRenderer: ({ data }: ICellRendererParams<ContractLine>) => {
        return `${data?.product?.stockCode} - ${data?.product?.stockName}`;
      }
    },
    {
      colId: 'reasonCode',
      headerName: 'Reason Name',
      field: 'statusChangeReason',
      initialWidth: 220,
      minWidth: 180,
      editable: true,
      cellRenderer: 'CellRenderer',
      cellEditor: 'DropdownEditor',
      cellEditorParams: ({ data }: ICellRendererParams<ContractLine>) => {
        // To show old reason inside dropdown even if it was already disabled
        // (user can accidentally change reason and want to return back old one)
        const contractLine = dnrContractLines.find((cl) => cl.contractLineID === data?.contractLineID);
        const reasons = [...reasonCodes];
        const isOldReasonAlreadyInDropdown =
          Boolean(reasons.find((el) => el.code === contractLine?.statusChangeReason?.code));

        if (!isOldReasonAlreadyInDropdown && contractLine?.statusChangeReason) {
          reasons.push(contractLine.statusChangeReason);
        }

        return {
          options: reasons,
          getOptionValue: (option: ReasonCode) => option.code,
          getOptionLabel: (option: ReasonCode) => option.name,
          saveOnChange: true,
          isLoading: isLoading
        };
      },
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine, ReasonCode>) => {
        return value?.name as string;
      },
    },
    {
      colId: 'dnrDate',
      headerName: 'DNR Date',
      field: 'statusChangeDate',
      initialWidth: 180,
      minWidth: 180,
      editable: true,
      cellEditor: 'DatepickerEditor',
      cellRenderer: 'CellRenderer',
      cellRendererParams: getCellRendererParams,
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      },
      cellEditorParams: () => ({
        saveOnChange: true,
      }),
    },
    {
      colId: 'comments',
      headerName: 'Comments',
      field: 'statusChangeComment',
      initialWidth: 140,
      minWidth: 140,
      editable: true,
      cellRenderer: 'CellRenderer',
      cellRendererParams: getCellRendererParams,
      valueSetter: (params: any) => {
        params.data.statusChangeComment = params.newValue.trim();
        return true;
      },
    },
    {
      colId: 'contractEndDate',
      headerName: 'Contract End Date',
      initialWidth: 150,
      minWidth: 150,
      cellRenderer: ({ context }: ICellRendererParams) => {
        const contractEndDate = (context.contract as Contract).contractEndDate;
        return contractEndDate ? dateCellRenderer({ value: contractEndDate }) : '';
      }
    },
    {
      colId: 'term',
      headerName: 'Contract Term',
      initialWidth: 125,
      minWidth: 125,
      cellRenderer: ({ context }: ICellRendererParams) => {
        const term = (context.contract as Contract).term;
        return term ? `${term} ${FormattingUtils.pluralize(term, 'month')}` : '';
      }
    },
    {
      colId: 'noFurtherBilling',
      field: 'noFurtherBilling',
      headerName: 'No Further Billing',
      initialWidth: 150,
      minWidth: 150,
      cellRenderer: NoFurtherBillingToggle,
      onCellValueChanged: ({ node }) => {
        node?.setDataValue('noFurtherBillingDate', null);
      }
    },
    {
      colId: 'noFurtherBillingDate',
      field: 'noFurtherBillingDate',
      headerName: 'Date (No Further Billing)',
      editable: ({ data }) => Boolean(data?.noFurtherBilling),
      cellRenderer: 'CellRenderer',
      cellEditor: 'DatepickerEditor',
      cellEditorParams: () => ({
        saveOnChange: true,
      }),
      cellRendererParams: getCellRendererParams,
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      },
    }
  ];
}
